import React, { MutableRefObject, MouseEvent, useState, useEffect } from "react";

import Crop from "@/lib/crop";

export type CanvasProps = {
  mouseUp: MouseEvent<HTMLDivElement> | undefined,
  baseDraw: () => void;
  onStartDrawing: () => void;
  onDrawZoomBox: (x: number, y: number, w: number, h: number) => void;
  onDrawCropBox: (x: number, y: number, w: number, h: number) => void;
  onWheelDown: () => void;
  onCanvasClick: (coords: Point) => void;
  viewOtherObjects: boolean;
  drawClip: Clip | null;
  cropClip: Clip | null;
  containerWidth: number;
  containerHeight: number;
  ratio: number;
  setIsDrawing: React.Dispatch<React.SetStateAction<boolean>>
}

type Clip = [number, number, number, number];
type Pair<A, B> = [A, B];
type Point = [number, number];

const MIN_DRAW_SIZE = 15;
const MIN_CROP_SIZE = 400;
const MIN_ZOOM_WIDTH = 300;
const MAX_IMAGE_HEIGHT = 600;

const Canvas = React.forwardRef<HTMLCanvasElement, CanvasProps>((props, ref) => {

  const [startCoordinates, setStartCoordinates] = useState<Point | null>(null);
  const [currentCoordinates, setCurrentCordinates] = useState<Point | null>(null);
  const [polygon, setPolygon] = useState<[Point, Point] | null>(null);
  const [isZooming, setIsZooming] = useState(false);
  const [isCropping, setIsCroping] = useState(false);
  const [cropSizeOk, setCropSizeOk] = useState(true);
  const [zoomSizeOk, setZoomSizeOk] = useState(false);
  const [size, setSize] = useState(0);
  const [startingCroppingState, setStartingCroppingState] = useState(true);
  const [realCropMinSize, setRealCropMinSize] = useState(MIN_CROP_SIZE)

  const {
    mouseUp,
    baseDraw,
    onStartDrawing,
    onDrawZoomBox,
    onDrawCropBox,
    onWheelDown,
    onCanvasClick,
    viewOtherObjects,
    drawClip,
    cropClip,
    containerWidth,
    containerHeight,
    ratio,
    setIsDrawing
  } = props;

  const getClickCoordinates = (evt: MouseEvent<HTMLCanvasElement> | MouseEvent<HTMLDivElement>) => {
    if (ref) {
      const rect = (ref as MutableRefObject<HTMLCanvasElement>).current.getBoundingClientRect();
      const x = evt.clientX - rect.left;
      const y = evt.clientY - rect.top;

      return [x, y] as Point;
    } else {
      return [0, 0] as Point
    }
  }

  const getCtx = () => {
    let ctx: CanvasRenderingContext2D | null = null;
    const hasCanvas = ref && (ref as MutableRefObject<HTMLCanvasElement>).current;
    if (hasCanvas) {
      ctx = (ref as MutableRefObject<HTMLCanvasElement>).current.getContext('2d');
    }
    return ctx;
  }

  const handleMouseDown = (evt: MouseEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    const coords = getClickCoordinates(evt);
    const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;
    const modifierKeyPressed = evt.getModifierState("Shift")

    if (modifierKeyPressed) {
      setIsCroping(true);
      //if out, start min distance away from border
      if ((coords[0] + realCropMinSize) > canvas.width) coords[0] = canvas.width - realCropMinSize
      if ((coords[1] + realCropMinSize) > canvas.height) coords[1] = canvas.height - realCropMinSize
      if ((coords[0] - realCropMinSize) < 0) coords[0] = realCropMinSize
      if ((coords[1] - realCropMinSize) < 0) coords[1] = realCropMinSize
      setIsDrawing(true);
    } else {
      setIsZooming(true);
      setIsDrawing(true);
    }
    setStartCoordinates(coords);

    const ctx = getCtx();
    if (ctx) {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      baseDraw();
    }
    onStartDrawing();
  }

  const handleClick = (evt: MouseEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    const coords = getClickCoordinates(evt);
    onCanvasClick(coords);
    setSize(0)
  }

  const handleMouseMove = (evt: MouseEvent<HTMLCanvasElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    if (isZooming || isCropping) {
      const coords = getClickCoordinates(evt);
      setCurrentCordinates(coords);
    }

    if (startingCroppingState && evt.buttons == 0) {
      const modifierKeyPressed = evt.getModifierState("Shift")
      const coords = getClickCoordinates(evt);
      const ctx = getCtx();
      const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;

      if (modifierKeyPressed && ctx) {
        //if out, don't move closer to border
        if ((coords[0] + realCropMinSize) > canvas.width) coords[0] = canvas.width - realCropMinSize
        if ((coords[1] + realCropMinSize) > canvas.height) coords[1] = canvas.height - realCropMinSize
        if ((coords[0] - realCropMinSize) < 0) coords[0] = realCropMinSize
        if ((coords[1] - realCropMinSize) < 0) coords[1] = realCropMinSize

        const crop = new Crop(coords[0] - realCropMinSize, coords[1] - realCropMinSize, 2 * realCropMinSize, 2 * realCropMinSize);
        setCurrentCordinates(coords);
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        baseDraw();
        drawRect(ctx, crop.x, crop.y, crop.w, crop.h);
        setSize(realCropMinSize)
      }
      else if (ctx && !cropClip) {
        ctx.clearRect(0, 0, canvas.width, canvas.height);
        baseDraw();
      }
    }
  }

  useEffect(() => {
    if (cropClip)
      setStartingCroppingState(false)
    else setStartingCroppingState(true)
  }, [cropClip])

  useEffect(() => {
    if (mouseUp) {
      handleMouseUp(mouseUp)
    }
  }, [mouseUp])

  const handleMouseUp = (evt: MouseEvent<HTMLCanvasElement> | MouseEvent<HTMLDivElement>) => {
    evt.preventDefault();
    evt.stopPropagation();
    if ((isZooming || isCropping) && startCoordinates) {
      const ctx = getCtx();
      const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;
      if (ctx && startCoordinates && currentCoordinates) {

        const w = currentCoordinates[0] - startCoordinates[0];
        const h = currentCoordinates[1] - startCoordinates[1];

        if (isZooming) {
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          baseDraw();
          // setPolygon([startCoordinates, coords]);

          const crop = new Crop(startCoordinates[0], startCoordinates[1], w, h);
          crop.keepRatio(ratio);
          crop.keepInBounds(canvas.width, canvas.height)
          if (zoomSizeOk) {
            onDrawZoomBox(crop.x, crop.y, crop.w, crop.h);
          }

        } else if (isCropping) {
          const crop = new Crop(startCoordinates[0] - size, startCoordinates[1] - size, 2 * size, 2 * size);

          const startPoint = [startCoordinates[0] - size, startCoordinates[1] - size] as Point;
          const endPoint = [startCoordinates[0] + size, startCoordinates[1] + size] as Point;


          ctx.clearRect(0, 0, canvas.width, canvas.height);
          baseDraw();
          if (cropSizeOk && size !== 0) {
            setPolygon([startPoint, endPoint]);
            onDrawCropBox(startCoordinates[0] - size, startCoordinates[1] - size, crop.w, crop.h);
          }
        }

      }

      if (isZooming) {
        setIsZooming(false);
        setIsDrawing(false);
      }
      if (isCropping) {
        setStartingCroppingState(true);
        setIsCroping(false);
        setIsDrawing(false);
      }
    }
  }

  useEffect(() => {
    if (drawClip) {
      const [bx, by, ex, ey] = drawClip
      setRealCropMinSize((MAX_IMAGE_HEIGHT * (MIN_CROP_SIZE / (ex - bx))) / 2 + 1)
    }
  }, [drawClip])

  const calculateRealCropSize = (x: number, y: number, w: number, h: number, currentDrawClip: Clip) => {
    if (currentDrawClip) {
      const [bx, by, ex, ey] = currentDrawClip;
      const clip_width = ex - bx;
      const clip_height = ey - by;

      // CROP relative coordinates
      const zoom_start_x = Math.floor(x);
      const zoom_start_y = Math.floor(y);
      const zoom_end_x = Math.floor(x + w);
      const zoom_end_y = Math.floor(y + h);

      // CLIP relative coordinates
      const clip_start_x = Math.floor((zoom_start_x / containerWidth) * (clip_width as number));
      const clip_start_y = Math.floor((zoom_start_y / containerHeight) * (clip_height as number));
      const clip_end_x = Math.floor((zoom_end_x / containerWidth) * (clip_width as number));
      const clip_end_y = Math.floor((zoom_end_y / containerHeight) * (clip_height as number));

      // IMAGE coordinates
      const real_start_x = bx + clip_start_x;
      const real_start_y = by + clip_start_y;
      const real_end_x = bx + clip_end_x;
      const real_end_y = by + clip_end_y;

      const real_width = real_end_x - real_start_x;
      const real_height = real_end_y - real_start_y;

      return [real_width, real_height]
    }
    else {
      return null;
    }
  }

  const handleMouseWheel = (evt: any) => {
    //@ts-ignore
    if (evt.deltaY > 0) {
      onWheelDown();
    }
  }

  useEffect(() => {
    const ctx = getCtx();
    const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current;
    if (ctx && startCoordinates && currentCoordinates) {

      // calculate size of the polygon
      const w = currentCoordinates[0] - startCoordinates[0];
      const h = currentCoordinates[1] - startCoordinates[1];

      const absW = Math.abs(w);
      const absH = Math.abs(h);

      var crop = new Crop(startCoordinates[0], startCoordinates[1], w, h);

      if (isZooming) {
        if (absW >= MIN_DRAW_SIZE || absH >= MIN_DRAW_SIZE) {
          crop.keepRatio(ratio);
          crop.keepInBounds(canvas.width, canvas.height)

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          baseDraw();
          drawRect(ctx, crop.x, crop.y, crop.w, crop.h);
        }
      } else if (isCropping) {
        if (absW * 2 >= MIN_DRAW_SIZE || absH * 2 >= MIN_DRAW_SIZE) {
          var newSize = Math.sqrt(crop.w * crop.w + crop.h * crop.h)

          if (newSize <= realCropMinSize && startingCroppingState) {
            newSize = realCropMinSize
          }
          else setStartingCroppingState(false)

          //if out, resize to the max possible size
          if ((startCoordinates[0] + newSize) > canvas.width) newSize = canvas.width - startCoordinates[0]
          if ((startCoordinates[1] + newSize) > canvas.height) newSize = canvas.height - startCoordinates[1]
          if ((startCoordinates[0] - newSize) < 0) newSize = startCoordinates[0]
          if ((startCoordinates[1] - newSize) < 0) newSize = startCoordinates[1]

          crop = new Crop(startCoordinates[0] - newSize, startCoordinates[1] - newSize, 2 * newSize, 2 * newSize);

          ctx.clearRect(0, 0, canvas.width, canvas.height);
          baseDraw();
          drawRect(ctx, crop.x, crop.y, crop.w, crop.h);
          setSize(newSize)
        }
      }

      if (drawClip) {
        const realCropSize = calculateRealCropSize(crop.x, crop.y, crop.w, crop.h, drawClip);
        if (realCropSize) {
          const realCropWidth = realCropSize[0];
          const realCropHeight = realCropSize[1];

          if (isCropping) {
            if (realCropHeight < MIN_CROP_SIZE || realCropWidth < MIN_CROP_SIZE) {
              setCropSizeOk(false);
            } else {
              setCropSizeOk(true);
            }
          } else if (isZooming) {
            if (realCropWidth < MIN_ZOOM_WIDTH) {
              setZoomSizeOk(false);
            } else {
              setZoomSizeOk(true);
            }
          }
        }
      }
    }
  }, [currentCoordinates]);

  useEffect(() => {
    const ctx = getCtx();
    if (ctx && polygon) {
      const canvas = (ref as MutableRefObject<HTMLCanvasElement>).current

      const [a, b] = polygon;
      const w = b[0] - a[0];
      const h = b[1] - a[1];
      const crop = new Crop(a[0], a[1], w, h);
      crop.makeSquare();
      crop.shrinkToFit(canvas.width, canvas.height);
      drawRect(ctx, crop.x, crop.y, crop.w, crop.h, "#FFF");
      // ctx.drawImage(current, 0, 0);
    }
  }, [polygon]);

  const drawRect = (ctx: CanvasRenderingContext2D, x: number, y: number, w: number, h: number, color?: string) => {
    ctx.lineWidth = 2;
    if (!isZooming && (startingCroppingState || isCropping) && !color) {
      ctx.setLineDash([6]);
    }
    ctx.strokeStyle = color ? color : isZooming ? (zoomSizeOk ? "#FFF" : "#F00") : isCropping ? (cropSizeOk ? "#0F0" : "#F00") : startingCroppingState ? "#0F0" : "#FFF";
    ctx.strokeRect(x, y, w, h);
  }

  return (
    <canvas
      ref={ref}
      style={{zIndex: 200, height: "100%", width: "100%"}}
      onMouseDown={handleMouseDown}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onWheel={handleMouseWheel}
      onClick={handleClick}
    />
  )
});

export default Canvas;